import React, { useState, useEffect } from "react";
import Advertise from "../pages/Advertise";
import Layout from '../pages/Layout';

const CarbonFootprintCalculator = () => {
  // State for input values
  const [userType, setUserType] = useState("individual");
  const [energyConsumption, setEnergyConsumption] = useState("");
  const [carDistance, setCarDistance] = useState("");
  const [flightDistance, setFlightDistance] = useState("");
  const [wasteGeneration, setWasteGeneration] = useState("");
  const [numPeople, setNumPeople] = useState(1);
  const [energyFactor, setEnergyFactor] = useState("");
  const [carFactor, setCarFactor] = useState("");
  const [flightFactor, setFlightFactor] = useState("");
  const [wasteFactor, setWasteFactor] = useState("");

  const [totalCarbonFootprint, setTotalCarbonFootprint] = useState(0);
  const [footprintPerPerson, setFootprintPerPerson] = useState(0);

  // Calculate carbon footprint whenever any input changes
  useEffect(() => {
    calculateCarbonFootprint();

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [
    energyConsumption,
    carDistance,
    flightDistance,
    wasteGeneration,
    numPeople,
    energyFactor,
    carFactor,
    flightFactor,
    wasteFactor,
    userType,
  ]);

  // Function to toggle between individual and business mode
  const toggleUserType = (type) => {
    setUserType(type);
    if (type === "individual") {
      setNumPeople(1); // Reset to 1 person when switching to individual
    }
  };

  // Function to calculate the total and per person carbon footprint
  const calculateCarbonFootprint = () => {
    const totalEmissions =
      energyConsumption * energyFactor +
      carDistance * carFactor +
      flightDistance * flightFactor +
      wasteGeneration * wasteFactor;

    const footprintPerPerson =
      numPeople > 1 ? totalEmissions / numPeople : totalEmissions;

    setTotalCarbonFootprint(totalEmissions);
    setFootprintPerPerson(footprintPerPerson);
  };

  return (
    <Layout title="Carbon Footprint Calculator" path="carbon-footprint">
      <section
        className="topics-detail-section section-padding"
        id="topics-detail"
      >
        <div className="container">
          <div className="row">
            {/* Carbon Footprint Calculator Section (75% space) */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Carbon Footprint Calculator
                </h3>
                <div className="container">
                  {/* Select Type Radio Buttons (Individual or Business) */}
                  <div className="row justify-content-center mb-4">
                    <div className="col-md-6 col-lg-4 text-center">
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="userType"
                          id="individual"
                          value="individual"
                          checked={userType === "individual"}
                          onChange={() => toggleUserType("individual")}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="individual"
                        >
                          Individual
                          {/* <span
                      className="ms-2 text-muted"
                      data-bs-toggle="popover"
                      data-bs-html="true"
                      data-bs-placement="top"
                      data-bs-content="<strong>Individual:</strong> Choose this option if you are calculating the carbon footprint for personal activities."
                      style={{ cursor: "pointer" }}
                    >
                      <i className="bi bi-info-circle"></i>
                    </span> */}
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="userType"
                          id="business"
                          value="business"
                          checked={userType === "business"}
                          onChange={() => toggleUserType("business")}
                        />
                        <label className="form-check-label" htmlFor="business">
                          Business
                          {/* <span
                      className="ms-2 text-muted"
                      data-bs-toggle="popover"
                      data-bs-html="true"
                      data-bs-placement="top"
                      data-bs-content="<strong>Business:</strong> Choose this option if you are calculating the carbon footprint for your organization's activities."
                      style={{ cursor: "pointer" }}
                    >
                      <i className="bi bi-info-circle"></i>
                    </span> */}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="row gy-3">
                    {/* Energy Consumption */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="energyConsumption" className="form-label">
                        Energy Consumption (kWh)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Energy Consumption:</strong> The amount of electricity consumed in kilowatt-hours (kWh)."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="energyConsumption"
                        step="0.01"
                        placeholder="Enter energy consumption"
                        value={energyConsumption}
                        onChange={(e) => setEnergyConsumption(e.target.value)}
                      />
                      {/* <small className="form-text text-muted">in kWh</small> */}
                    </div>

                    {/* Car Travel Distance */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="carDistance" className="form-label">
                        Car Travel Distance (km)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Car Travel Distance:</strong> The distance traveled by car in kilometers (km)."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="carDistance"
                        step="0.01"
                        placeholder="Enter car travel distance"
                        value={carDistance}
                        onChange={(e) => setCarDistance(e.target.value)}
                      />
                      {/* <small className="form-text text-muted">in km</small> */}
                    </div>

                    {/* Flight Distance */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="flightDistance" className="form-label">
                        Flight Travel Distance (km)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Flight Travel Distance:</strong> The distance traveled by flight in kilometers (km)."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="flightDistance"
                        step="0.01"
                        placeholder="Enter flight travel distance"
                        value={flightDistance}
                        onChange={(e) => setFlightDistance(e.target.value)}
                      />
                      {/* <small className="form-text text-muted">in km</small> */}
                    </div>

                    {/* Waste Generation */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="wasteGeneration" className="form-label">
                        Waste Generation (kg)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Waste Generation:</strong> The amount of waste generated in kilograms (kg)."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="wasteGeneration"
                        step="0.01"
                        placeholder="Enter waste generation"
                        value={wasteGeneration}
                        onChange={(e) => setWasteGeneration(e.target.value)}
                      />
                      {/* <small className="form-text text-muted">in kg</small> */}
                    </div>

                    {/* Number of People (For Business Calculation) */}
                    {userType === "business" && (
                      <div className="col-md-6 col-lg-4">
                        <label htmlFor="numPeople" className="form-label">
                          Number of People
                          <span
                      className="ms-2 text-muted"
                      data-bs-toggle="popover"
                      data-bs-html="true"
                      data-bs-placement="top"
                      data-bs-content="<strong>Number of People:</strong> Enter the number of people for business-related calculations."
                      style={{ cursor: "pointer" }}
                    >
                      <i className="bi bi-info-circle"></i>
                    </span>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="numPeople"
                          min="1"
                          placeholder="Enter number of people"
                          value={numPeople}
                          onChange={(e) => setNumPeople(e.target.value)}
                        />
                      </div>
                    )}

                    {/* Carbon Factor for Energy (kg CO₂/kWh) */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="energyFactor" className="form-label">
                        Energy Carbon Factor
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Energy Carbon Factor:</strong> The amount of CO₂ emitted per kWh of energy consumed."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="energyFactor"
                        step="0.01"
                        placeholder="Enter energy carbon factor"
                        value={energyFactor}
                        onChange={(e) => setEnergyFactor(e.target.value)}
                      />
                      {/* <small className="form-text text-muted">
                        in kg CO₂/kWh
                      </small> */}
                    </div>

                    {/* Carbon Factor for Car Travel (kg CO₂/km) */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="carFactor" className="form-label">
                        Car Travel Carbon Factor
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Car Travel Carbon Factor:</strong> The amount of CO₂ emitted per kilometer driven by car."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="carFactor"
                        step="0.01"
                        placeholder="Enter car carbon factor"
                        value={carFactor}
                        onChange={(e) => setCarFactor(e.target.value)}
                      />
                      {/* <small className="form-text text-muted">
                        in kg CO₂/km
                      </small> */}
                    </div>

                    {/* Carbon Factor for Flight Travel (kg CO₂/km) */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="flightFactor" className="form-label">
                        Flight Travel Carbon Factor
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Flight Travel Carbon Factor:</strong> The amount of CO₂ emitted per kilometer traveled by flight."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="flightFactor"
                        step="0.01"
                        placeholder="Enter flight carbon factor"
                        value={flightFactor}
                        onChange={(e) => setFlightFactor(e.target.value)}
                      />
                      {/* <small className="form-text text-muted">
                        in kg CO₂/km
                      </small> */}
                    </div>

                    {/* Carbon Factor for Waste (kg CO₂/kg) */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="wasteFactor" className="form-label">
                        Waste Carbon Factor
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Waste Carbon Factor:</strong> The amount of CO₂ emitted per kilogram of waste generated."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="wasteFactor"
                        step="0.01"
                        placeholder="Enter waste carbon factor"
                        value={wasteFactor}
                        onChange={(e) => setWasteFactor(e.target.value)}
                      />
                      {/* <small className="form-text text-muted">
                        in kg CO₂/kg
                      </small> */}
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Carbon Footprint (kg CO₂):{" "}
                        <span className="fw-bold ms-3">
                          {totalCarbonFootprint.toFixed(2)}
                        </span>
                      </h6>
                      <h6>
                        Carbon Footprint per Person (kg CO₂):{" "}
                        <span className="fw-bold ms-3">
                          {footprintPerPerson.toFixed(2)}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section (25% space) */}
            <Advertise />
          </div>
        </div>
      </section>
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Carbon Footprint Calculator
                </h3>

                <h4 className="mt-3 mb-3">What is a Carbon Footprint?</h4>
                <p>
                  A **carbon footprint** refers to the total amount of
                  greenhouse gases (GHGs), primarily **carbon dioxide (CO₂)**,
                  that are emitted into the atmosphere due to human activities.
                  This includes energy consumption, transportation, waste
                  generation, and more. The carbon footprint is a key measure of
                  your impact on the environment and is used to understand how
                  much you contribute to global warming.
                </p>

                <h4 className="mt-4 mb-3">
                  How to Calculate Your Carbon Footprint?
                </h4>
                <p>
                  To calculate your carbon footprint, you need to evaluate the
                  following:
                </p>
                <ul>
                  <li>Energy consumption (in kilowatt-hours, kWh)</li>
                  <li>Car travel distance (in kilometers, km)</li>
                  <li>Flight distance (in kilometers, km)</li>
                  <li>Waste generation (in kilograms, kg)</li>
                </ul>
                <p>
                  Each of these values is then multiplied by specific **carbon
                  factors** that represent the emissions for each unit of energy
                  or activity. These factors help estimate the amount of CO₂
                  released for each type of activity, allowing you to calculate
                  the total emissions.
                </p>

                <h4 className="mt-4 mb-3">
                  What are the Key Factors in Calculating a Carbon Footprint?
                </h4>
                <p>
                  The carbon footprint calculation depends on several factors,
                  including:
                </p>
                <ul>
                  <li>
                    <strong>Energy Consumption:</strong> The amount of energy
                    you use, whether it’s from electricity, gas, or other
                    sources.
                  </li>
                  <li>
                    <strong>Transportation:</strong> The distance traveled by
                    car, flight, or other means of transport. This includes both
                    personal and business-related travel.
                  </li>
                  <li>
                    <strong>Waste Generation:</strong> The amount of waste you
                    generate, as waste disposal also contributes to emissions.
                  </li>
                  <li>
                    <strong>Carbon Factors:</strong> Specific factors for each
                    activity that quantify emissions, like kWh of energy used or
                    km driven in a car.
                  </li>
                </ul>

                <h4 className="mt-4 mb-3">
                  Why is Calculating Your Carbon Footprint Important?
                </h4>
                <p>
                  Understanding your carbon footprint is important for several
                  reasons:
                </p>
                <ul>
                  <li>
                    <strong>Environmental Impact:</strong> By measuring your
                    carbon footprint, you can see how much your activities
                    contribute to climate change and global warming.
                  </li>
                  <li>
                    <strong>Reduce Emissions:</strong> Calculating your carbon
                    footprint allows you to identify areas where you can reduce
                    emissions and make environmentally conscious decisions.
                  </li>
                  <li>
                    <strong>Take Action:</strong> Reducing your carbon footprint
                    is key to slowing down climate change and protecting the
                    planet for future generations.
                  </li>
                </ul>

                <h4 className="mt-4 mb-3">
                  What are the Benefits of Using a Carbon Footprint Calculator?
                </h4>
                <p>
                  Using a Carbon Footprint Calculator provides several benefits:
                </p>
                <ul>
                  <li>
                    <strong>Track Your Impact:</strong> The calculator helps
                    track your environmental impact, giving you the data you
                    need to make informed decisions.
                  </li>
                  <li>
                    <strong>Identify Reduction Opportunities:</strong> By
                    calculating your footprint, you can identify specific areas
                    where you can reduce emissions, like using less energy or
                    switching to eco-friendly transportation.
                  </li>
                  <li>
                    <strong>Promote Sustainability:</strong> Encourages
                    sustainable living by helping individuals and businesses
                    make greener choices, contributing to long-term
                    environmental benefits.
                  </li>
                  <li>
                    <strong>Transparency:</strong> Provides transparency by
                    clearly showing where your emissions are coming from,
                    helping you understand how your actions affect the planet.
                  </li>
                </ul>

                <h4 className="mt-4 mb-3">
                  How Can I Offset My Carbon Footprint?
                </h4>
                <p>
                  Offsetting your carbon footprint involves investing in
                  projects that reduce or capture CO₂ emissions, such as
                  planting trees or supporting renewable energy projects. Carbon
                  offsetting can be an effective way to balance your emissions,
                  but reducing your footprint at the source should always be the
                  priority.
                </p>

                <p>
                  In summary, the **Carbon Footprint Calculator** is an
                  essential tool for understanding and reducing the
                  environmental impact of your activities. Whether you’re an
                  individual, business, or organization, it helps you take steps
                  toward a more sustainable future by measuring and minimizing
                  your carbon emissions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CarbonFootprintCalculator;
